import type { AuthoredGridPose, CoasterProject } from '../schema/index.ts';
import { AUTHORED_TRACK_CATALOG } from './catalog.ts';
import {
  descriptorExitPose,
  instantiateSpans,
  normalizeHeadingStep,
  posesConnect,
} from './fixed-grid.ts';
import { authoredForward, authoredRight } from './directions.ts';
import {
  appendAuthoredCandidate,
  appendAuthoredPiece,
  authoredEndPose,
  closeAuthoredCircuit,
} from './operations.ts';
import type {
  AuthoredBuildResult,
  AuthoredPieceCandidate,
  AuthoredPieceDescriptor,
  AuthoredSpanTemplate,
  FixedPoint,
} from './types.ts';

export interface AuthoredFinishPlan {
  readonly descriptorIds: readonly string[];
  readonly exploredStates: number;
  readonly project: CoasterProject;
  readonly mode: 'standard' | 'hybrid';
  readonly customTailSectionIds: readonly string[];
}

export interface AuthoredFinishLimits {
  readonly maxPieces?: number;
  readonly maxStates?: number;
  readonly timeBudgetMs?: number;
  readonly validateProject?: (project: CoasterProject) => string | null;
}

const FINISH_DESCRIPTOR_IDS = new Set([
  'straight.1t',
  'straight.2t',
  'straight.3t',
  'turn.left.45.small',
  'turn.right.45.small',
  'turn.left.90.small',
  'turn.right.90.small',
  'turn.left.45.medium',
  'turn.right.45.medium',
  'turn.left.90.medium',
  'turn.right.90.medium',
  'slope.0.25',
  'slope.25.60',
  'slope.60.90',
  'slope.90.60',
  'slope.60.25',
  'slope.25.0',
  'slope.0.n25',
  'slope.n25.n60',
  'slope.n60.n90',
  'slope.n90.n60',
  'slope.n60.n25',
  'slope.n25.0',
  'slope.25.hold.1t',
  'slope.n25.hold.1t',
  'slope.60.hold.1t',
  'slope.n60.hold.1t',
  'bank.level.left.1t',
  'bank.left.level.1t',
  'bank.level.right.1t',
  'bank.right.level.1t',
]);

const FINISH_DESCRIPTORS = AUTHORED_TRACK_CATALOG.filter((descriptor) =>
  FINISH_DESCRIPTOR_IDS.has(descriptor.id),
);

function poseKey(pose: AuthoredGridPose): string {
  return [
    pose.x,
    pose.y,
    pose.z,
    pose.headingStep,
    pose.pitchDegrees,
    pose.bank,
    pose.orientation,
    pose.windingQuarterTurns,
  ].join('|');
}

function heuristic(pose: AuthoredGridPose, target: AuthoredGridPose): number {
  const spatial =
    (Math.abs(pose.x - target.x) + Math.abs(pose.y - target.y) + Math.abs(pose.z - target.z)) / 32;
  const headingDelta = Math.min(
    Math.abs(pose.headingStep - target.headingStep),
    8 - Math.abs(pose.headingStep - target.headingStep),
  );
  const pitchPenalty = pose.pitchDegrees === target.pitchDegrees ? 0 : 3;
  const bankPenalty = pose.bank === target.bank ? 0 : 2;
  return spatial + headingDelta + pitchPenalty + bankPenalty;
}

function inBounds(project: CoasterProject, pose: AuthoredGridPose): boolean {
  const halfX = Math.floor((project.plot.sizeMeters.x / 2) * 8);
  const halfZ = Math.floor((project.plot.sizeMeters.z / 2) * 8);
  const ground = Math.round(project.plot.groundElevationMeters * 8);
  return (
    pose.x >= -halfX &&
    pose.x <= halfX &&
    pose.z >= -halfZ &&
    pose.z <= halfZ &&
    pose.y >= ground + 16 &&
    pose.y <= ground + 1_600
  );
}

interface SearchState {
  readonly pose: AuthoredGridPose;
  readonly path: readonly string[];
  readonly score: number;
}

function targetInLocalFrame(entry: AuthoredGridPose, target: AuthoredGridPose): FixedPoint | null {
  const forward = authoredForward(entry.headingStep);
  const right = authoredRight(entry.headingStep);
  const dx = target.x - entry.x;
  const dz = target.z - entry.z;
  const denominator = forward.x ** 2 + forward.z ** 2;
  const x = (dx * forward.x + dz * forward.z) / denominator;
  const z = (dx * right.x + dz * right.z) / denominator;
  if (!Number.isInteger(x) || !Number.isInteger(z)) return null;
  return { x, y: target.y - entry.y, z };
}

function sampledCubicSpans(
  end: FixedPoint,
  control1: FixedPoint,
  control2: FixedPoint,
  segmentCount = 6,
): readonly AuthoredSpanTemplate[] {
  const pointAt = (progress: number) => {
    const inverse = 1 - progress;
    return {
      x:
        3 * inverse ** 2 * progress * control1.x +
        3 * inverse * progress ** 2 * control2.x +
        progress ** 3 * end.x,
      y:
        3 * inverse ** 2 * progress * control1.y +
        3 * inverse * progress ** 2 * control2.y +
        progress ** 3 * end.y,
      z:
        3 * inverse ** 2 * progress * control1.z +
        3 * inverse * progress ** 2 * control2.z +
        progress ** 3 * end.z,
    };
  };
  const derivativeAt = (progress: number) => {
    const inverse = 1 - progress;
    return {
      x:
        3 * inverse ** 2 * control1.x +
        6 * inverse * progress * (control2.x - control1.x) +
        3 * progress ** 2 * (end.x - control2.x),
      y:
        3 * inverse ** 2 * control1.y +
        6 * inverse * progress * (control2.y - control1.y) +
        3 * progress ** 2 * (end.y - control2.y),
      z:
        3 * inverse ** 2 * control1.z +
        6 * inverse * progress * (control2.z - control1.z) +
        3 * progress ** 2 * (end.z - control2.z),
    };
  };
  const controlScale = 1 / (segmentCount * 3);
  return Array.from({ length: segmentCount }, (_, index) => {
    const startProgress = index / segmentCount;
    const endProgress = (index + 1) / segmentCount;
    const start = pointAt(startProgress);
    const next = pointAt(endProgress);
    const startDerivative = derivativeAt(startProgress);
    const endDerivative = derivativeAt(endProgress);
    return {
      end: {
        x: Math.round(next.x),
        y: Math.round(next.y),
        z: Math.round(next.z),
      },
      control1: {
        x: Math.round(start.x + startDerivative.x * controlScale),
        y: Math.round(start.y + startDerivative.y * controlScale),
        z: Math.round(start.z + startDerivative.z * controlScale),
      },
      control2: {
        x: Math.round(next.x - endDerivative.x * controlScale),
        y: Math.round(next.y - endDerivative.y * controlScale),
        z: Math.round(next.z - endDerivative.z * controlScale),
      },
    };
  });
}

function autocompleteTailDescriptor(
  entry: AuthoredGridPose,
  target: AuthoredGridPose,
): AuthoredPieceDescriptor | null {
  if (
    entry.pitchDegrees !== 0 ||
    target.pitchDegrees !== 0 ||
    entry.bank !== 'level' ||
    target.bank !== 'level' ||
    entry.orientation !== target.orientation
  )
    return null;
  const end = targetInLocalFrame(entry, target);
  if (!end) return null;
  const distance = Math.hypot(end.x, end.y, end.z);
  if (distance < 16 || distance > 192 || Math.abs(end.y) > 64) return null;

  const headingDelta = normalizeHeadingStep(target.headingStep - entry.headingStep);
  const exitDirection = authoredForward(headingDelta);
  const exitMagnitude = Math.hypot(exitDirection.x, exitDirection.z);
  const handleLength = Math.max(16, Math.min(64, Math.round(distance * 0.36)));
  const control1 = { x: handleLength, y: 0, z: 0 };
  const control2 = {
    x: Math.round(end.x - (exitDirection.x / exitMagnitude) * handleLength),
    y: end.y,
    z: Math.round(end.z - (exitDirection.z / exitMagnitude) * handleLength),
  };
  return {
    id: 'autocomplete.freeform.v1',
    label: 'Final Station Alignment',
    category: 'straight',
    family: 'autocomplete_tail',
    description:
      'Constrained final alignment used only after standard pieces reach the closest safe pose.',
    entry: {
      pitches: [entry.pitchDegrees],
      banks: [entry.bank],
      orientations: [entry.orientation],
    },
    exit: {
      headingDelta,
      pitch: target.pitchDegrees,
      bank: target.bank,
      orientation: target.orientation,
      windingQuarterTurnsDelta: 0,
    },
    spans: sampledCubicSpans(end, control1, control2),
    allowedTrackTypes: ['normal'],
    tags: ['autocomplete', 'freeform-tail'],
    supportStyle: 'standard',
  };
}

function appendPath(
  project: CoasterProject,
  descriptorIds: readonly string[],
): AuthoredBuildResult<CoasterProject> {
  let candidate = project;
  for (const descriptorId of descriptorIds) {
    const appended = appendAuthoredPiece(candidate, descriptorId);
    if (!appended.ok) return appended;
    candidate = appended.value;
  }
  return { ok: true, value: candidate };
}

function appendAutocompleteTail(
  project: CoasterProject,
  target: AuthoredGridPose,
): AuthoredBuildResult<{ project: CoasterProject; sectionIds: readonly string[] }> {
  const entry = authoredEndPose(project);
  if (!entry)
    return { ok: false, code: 'missing_entry', message: 'The track has no finish endpoint.' };
  const descriptor = autocompleteTailDescriptor(entry, target);
  if (!descriptor)
    return {
      ok: false,
      code: 'unsafe_tail',
      message: 'The remaining station gap is too large or misaligned for a safe final curve.',
    };
  const beforeSections = new Set(project.track.sections.map((section) => section.id));
  const candidate: AuthoredPieceCandidate = {
    descriptor,
    entryPose: entry,
    exitPose: { ...target, windingQuarterTurns: entry.windingQuarterTurns },
    spans: instantiateSpans(entry, descriptor),
    behavior: { kind: 'normal' },
  };
  const appended = appendAuthoredCandidate(project, candidate);
  if (!appended.ok) return appended;
  const closed = appended.value.track.closed
    ? ({ ok: true, value: appended.value } as const)
    : closeAuthoredCircuit(appended.value);
  if (!closed.ok) return closed;
  return {
    ok: true,
    value: {
      project: closed.value,
      sectionIds: closed.value.track.sections
        .filter((section) => !beforeSections.has(section.id))
        .map((section) => section.id),
    },
  };
}

function planAuthoredRoute(
  project: CoasterProject,
  limits: AuthoredFinishLimits = {},
  allowHybrid = false,
): AuthoredBuildResult<AuthoredFinishPlan> {
  if (project.track.geometryKind !== 'authored_grid_v2')
    return {
      ok: false,
      code: 'wrong_geometry_kind',
      message: 'This finish solver only supports authored-grid tracks.',
    };
  if (project.track.closed)
    return { ok: false, code: 'already_closed', message: 'This circuit is already closed.' };
  const station = project.track.pieces[0];
  const start = authoredEndPose(project);
  if (!station || station.descriptorId !== 'station.2t' || !start)
    return { ok: false, code: 'station_required', message: 'Place a station first.' };
  if (project.track.pieces.length < 4)
    return {
      ok: false,
      code: 'needs_track',
      message: 'Build at least three pieces after the station first.',
    };
  const target = station.entryPose;
  const maxPieces = limits.maxPieces ?? 12;
  const maxStates = limits.maxStates ?? 25_000;
  const deadline = Date.now() + (limits.timeBudgetMs ?? 100);
  const queue: SearchState[] = [{ pose: start, path: [], score: heuristic(start, target) }];
  const bestDepth = new Map<string, number>([[poseKey(start), 0]]);
  let exploredStates = 0;
  let bestHybrid: {
    readonly state: SearchState;
    readonly distance: number;
    readonly headingDelta: number;
  } | null = null;
  let lastRejectedRoute: string | null = null;

  while (queue.length && exploredStates < maxStates && Date.now() <= deadline) {
    queue.sort((a, b) => a.score - b.score || a.path.length - b.path.length);
    const current = queue.shift()!;
    exploredStates += 1;
    if (posesConnect(current.pose, target)) {
      const appended = appendPath(project, current.path);
      if (!appended.ok) return appended;
      const candidate = appended.value;
      const closed = candidate.track.closed
        ? ({ ok: true, value: candidate } as const)
        : closeAuthoredCircuit(candidate);
      if (!closed.ok) return closed;
      const rejection = limits.validateProject?.(closed.value) ?? null;
      if (rejection) {
        lastRejectedRoute = rejection;
        continue;
      }
      return {
        ok: true,
        value: {
          descriptorIds: current.path,
          exploredStates,
          project: closed.value,
          mode: 'standard',
          customTailSectionIds: [],
        },
      };
    }
    if (allowHybrid && autocompleteTailDescriptor(current.pose, target)) {
      const distance = Math.hypot(
        current.pose.x - target.x,
        current.pose.y - target.y,
        current.pose.z - target.z,
      );
      const headingDelta = Math.min(
        Math.abs(current.pose.headingStep - target.headingStep),
        8 - Math.abs(current.pose.headingStep - target.headingStep),
      );
      if (
        !bestHybrid ||
        distance < bestHybrid.distance ||
        (distance === bestHybrid.distance && headingDelta < bestHybrid.headingDelta) ||
        (distance === bestHybrid.distance &&
          headingDelta === bestHybrid.headingDelta &&
          current.path.length < bestHybrid.state.path.length)
      )
        bestHybrid = { state: current, distance, headingDelta };
    }
    if (current.path.length >= maxPieces) continue;
    for (const descriptor of FINISH_DESCRIPTORS) {
      if (!descriptor.entry.pitches.includes(current.pose.pitchDegrees)) continue;
      if (!descriptor.entry.banks.includes(current.pose.bank)) continue;
      if (!descriptor.entry.orientations.includes(current.pose.orientation)) continue;
      const nextPose = descriptorExitPose(current.pose, descriptor);
      if (!inBounds(project, nextPose)) continue;
      const nextPath = [...current.path, descriptor.id];
      const key = poseKey(nextPose);
      if (!posesConnect(nextPose, target)) {
        if ((bestDepth.get(key) ?? Number.POSITIVE_INFINITY) <= nextPath.length) continue;
        bestDepth.set(key, nextPath.length);
      }
      queue.push({
        pose: nextPose,
        path: nextPath,
        score: nextPath.length + heuristic(nextPose, target),
      });
    }
  }
  if (allowHybrid && bestHybrid) {
    const standard = appendPath(project, bestHybrid.state.path);
    if (!standard.ok) return standard;
    const completed = appendAutocompleteTail(standard.value, target);
    if (!completed.ok) return completed;
    return {
      ok: true,
      value: {
        descriptorIds: bestHybrid.state.path,
        exploredStates,
        project: completed.value.project,
        mode: 'hybrid',
        customTailSectionIds: completed.value.sectionIds,
      },
    };
  }
  return {
    ok: false,
    code: Date.now() > deadline ? 'timeout' : 'no_standard_route',
    message: lastRejectedRoute
      ? `Standard routes reach the station but fail the build check: ${lastRejectedRoute}`
      : 'No tested standard-piece route reaches the station from this exact height, heading, slope, and bank.',
  };
}

export function planAuthoredFinish(
  project: CoasterProject,
  limits: AuthoredFinishLimits = {},
): AuthoredBuildResult<AuthoredFinishPlan> {
  return planAuthoredRoute(project, limits, false);
}

export function planAuthoredAutocomplete(
  project: CoasterProject,
  limits: AuthoredFinishLimits = {},
): AuthoredBuildResult<AuthoredFinishPlan> {
  return planAuthoredRoute(project, limits, true);
}
